import React, { useEffect, useRef } from "react";
import { useChatStore } from "../../store/chatStore";
import { useUIStore } from "../../store/uiStore";
import MessageBubble from "./MessageBubble";
import { WelcomeScreen } from "../welcome/WelcomeScreen";
import { CategoryType, QuickActionTemplate } from "../welcome/types";
import { trackEvent } from "../../services/analyticsService";
import { logDebug, logError } from "../../utils/logging";
import styles from "./MessageList.module.css"; // Import the CSS module
import { useGuidedFlowStore } from "../../store/guidedFlowStore";
import { GuidedPromptFlow } from "../welcome/GuidedPromptFlow";
import { useMindspaceStore } from "../../store/mindspaceStore";

const MessageList: React.FC = () => {
  const messages = useChatStore((state) => state.messages);
  const currentAIMessage = useChatStore((state) => state.currentAIMessage);
  const isLoadingHistory = useChatStore((state) => state.isLoadingHistory);
  const historyError = useChatStore((state) => state.historyError);
  const streamError = useChatStore((state) => state.streamError);
  const sendMessage = useChatStore((state) => state.sendMessage);
  const sendMessageWithMindspace = useChatStore((state) => state.sendMessageWithMindspace);
  
  const setIsInitializingChat = useUIStore((state) => state.setIsInitializingChat);

  // Guided flow store selectors
  const {
    status: guidedStatus,
    activeFlow,
    completeFlow,
    abandonFlow,
    startFlow,
  } = useGuidedFlowStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasTrackedWelcomeView = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentAIMessage]); // Scroll on new messages or when AI message updates

  // Track welcome screen view
  // BUGFIX F2.5.0: Show welcome screen for new sessions even if history error exists
  // History errors are expected for brand new sessions, so don't block welcome screen
  const shouldShowWelcomeScreen = messages.length === 0 &&
    !currentAIMessage &&
    !isLoadingHistory &&
    guidedStatus === 'idle';

  useEffect(() => {
    if (shouldShowWelcomeScreen && !hasTrackedWelcomeView.current) {
      trackEvent('welcome_screen_viewed');
      hasTrackedWelcomeView.current = true;
    }
  }, [shouldShowWelcomeScreen]);

  const handleWelcomeActionSelect = async (cardType: string, category?: CategoryType, template?: QuickActionTemplate) => {
    logDebug('[MessageList] Welcome action selected:', { cardType, category, template: template?.template_id });

    // Track the interaction
    trackEvent('quick_action_card_clicked', { 
      card_type: cardType,
      category: category || 'none',
      template_id: template?.template_id || 'none'
    });

    // Set initializing state
    setIsInitializingChat(true);

    try {
      // If we have a specific template, handle its logic first for clear type safety
      if (template) {
        // If the template has associated questions, launch the guided flow
        if (template.questions && template.questions.length > 0) {
          logDebug('[MessageList] Starting guided flow for template:', template.template_id);
          startFlow(template);
          trackEvent('quick_action_template_guided_flow_started', {
            template_id: template.template_id,
            card_type: template.card_type,
            category: template.category,
            total_questions: template.questions.length,
          });
          setIsInitializingChat(false);
          return; // Exit here
        }

        // No questions -> fall back to immediate prompt
        logDebug('[MessageList] Using template initial prompt (no questions):', template.template_id);
        const message = template.initial_prompt;
        
        trackEvent('quick_action_template_used', {
          template_id: template.template_id,
          card_type: template.card_type,
          category: template.category
        });

        const mindspaceId = useMindspaceStore.getState().defaultMindspace?.id;
        if (!mindspaceId) {
          throw new Error('Mindspace-ul nu este disponibil. Vă rugăm să reîncercați mai târziu.');
        }

        await sendMessageWithMindspace(message, mindspaceId, template.template_id);
      } else {
        // Fallback to category-based messages for direct actions (no template)
        let message = '';
        if (cardType === 'creaza_draft') {
          if (category === 'Copywriting') {
            message = 'Vreau să creez copy pentru marketing. Ajută-mă cu copywriting.';
          } else if (category === 'Document') {
            message = 'Vreau să creez documente pentru marketing. Ajută-mă cu documentele.';
          } else if (category === 'Altele') {
            message = 'Vreau să creez alte tipuri de conținut pentru marketing.';
          } else {
            message = 'Vreau să creez un draft pentru marketing. Ce opțiuni ai pentru mine?';
          }
        } else if (cardType === 'sfaturi_strategice') {
          message = 'Vreau să obțin sfaturi strategice pentru afacerea mea. Poți să mă ajuți cu consultanță strategică?';
        } else if (cardType === 'sugereste_curs') {
          message = 'Vreau recomandări de cursuri pentru dezvoltarea mea în marketing. Ce cursuri îmi sugerezi?';
        } else {
          message = 'Salut! Vreau să încep o conversație despre marketing.';
        }
        
        logDebug('[MessageList] Sending generic message:', message);
        await sendMessage(message);
      }

      // Track successful completion
      trackEvent('quick_action_flow_started', { 
        card_type: cardType,
        category: category || 'none',
        template_id: template ? template.template_id : 'none'
      });

    } catch (error) {
      logError('[MessageList] Error starting quick action flow:', error);
      trackEvent('quick_action_error', { 
        card_type: cardType,
        category: category || 'none',
        template_id: template ? template.template_id : 'none',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsInitializingChat(false);
    }
  };

  const messageListStyle: React.CSSProperties = {
    flexGrow: 1,
    overflowY: "auto",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  };

  if (isLoadingHistory) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Se încarcă istoricul...
      </div>
    );
  }

  // BUGFIX F2.5.0: Only display history error for sessions that should have messages
  // For new sessions (no messages), show welcome screen instead of history error
  // History errors during initial load of empty sessions are expected and should not block welcome screen
  if (historyError && messages.length > 0 && !currentAIMessage) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
        Eroare la încărcarea istoricului: {historyError}
      </div>
    );
  }

  // Show WelcomeScreen for empty chat
  if (shouldShowWelcomeScreen) {
    return (
      <div className={styles.welcomeScreenContainer}>
        <WelcomeScreen onActionSelect={handleWelcomeActionSelect} />
      </div>
    );
  }

  // Show GuidedPromptFlow only while actively collecting or finalizing answers.
  if ((guidedStatus === 'collecting' || guidedStatus === 'finalizing') && activeFlow) {
    return (
      <GuidedPromptFlow
        template={activeFlow.template}
        isActive={true}
        onComplete={() => {
          try {
            completeFlow();
          } catch (e) {
            logError('[MessageList] Guided flow completion error', e);
          }
        }}
        onCancel={() => {
          abandonFlow();
        }}
      />
    );
  }

  return (
    <div style={messageListStyle}>
      {messages.map((msg) => (
        <MessageBubble key={msg.id || msg.created_at} message={msg} />
      ))}
      {currentAIMessage && (
        <MessageBubble key="current-ai-message" message={currentAIMessage} />
      )}
      {/* Display stream error if it occurs, typically below the messages */}
      {streamError && (
        <div style={{ padding: "10px", textAlign: "center", color: "red" }}>
          {streamError.includes("Google API key not configured")
            ? "Serviciul AI nu este configurat corect. Te rugăm să contactezi suportul."
            : streamError.includes("Error:")
              ? streamError
              : "Eroare la conectarea cu AI. Te rugăm să încerci mai târziu."}
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
